import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { queryClient } from '@/lib/query-client';

/** Evita cache do createJSONStorage apontar para dummyStorage após SSR. */
const actingWebStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(name, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(name);
  },
};

/**
 * O profissional que o dono está vendo. Nome e foto vão junto para a pill
 * "Vendo agenda de…" aparecer sem esperar a lista de membros.
 */
export interface ActingProvider {
  providerId: string;
  name: string;
  image: string | null;
  color: string | null;
}

interface ActingProviderState {
  /** `null` = o próprio usuário. */
  acting: ActingProvider | null;
}

type ActingPersisted = Pick<ActingProviderState, 'acting'>;

const actingStorage =
  Platform.OS === 'web'
    ? createJSONStorage<ActingPersisted>(() => actingWebStorage)
    : createJSONStorage<ActingPersisted>(() => AsyncStorage);

export const useActingProviderStore = create<ActingProviderState>()(
  persist(
    (): ActingProviderState => ({
      acting: null,
    }),
    {
      name: 'acting-provider-storage',
      storage: actingStorage,
    },
  ),
);

/** Id que vai no header `X-Acting-Provider`; `null` quando é o próprio usuário. */
export function getActingProviderId(): string | null {
  return useActingProviderStore.getState().acting?.providerId ?? null;
}

/**
 * Config de request para agir como um profissional só nesta chamada, sem
 * trocar o foco ("Com quem?" no novo agendamento). Vale também com o próprio
 * providerId: é o jeito de falar por si enquanto o foco está em outro.
 */
export function actAsHeaders(providerId: string | null | undefined) {
  return providerId ? { headers: { 'X-Acting-Provider': providerId } } : {};
}

/**
 * Troca o foco. Todo o cache é de um profissional só, então ele é zerado e as
 * telas montadas buscam de novo — sem chave de query nova em cada hook e sem
 * dado de um aparecer na tela do outro. `resetQueries` (e não `clear`) porque
 * `clear` tira as queries do cache sem avisar quem está montado, e a tela
 * ficaria parada nos dados antigos.
 */
export function switchActingProvider(next: ActingProvider | null): void {
  const current = useActingProviderStore.getState().acting;
  if ((current?.providerId ?? null) === (next?.providerId ?? null)) {
    // Mesmo profissional: só atualiza nome/foto, sem refazer nada.
    if (next) useActingProviderStore.setState({ acting: next });
    return;
  }
  useActingProviderStore.setState({ acting: next });
  void queryClient.resetQueries();
}

/** Sai do foco sem mexer no cache: logout, ou foco que não vale mais. */
export function resetActingProvider(): void {
  useActingProviderStore.setState({ acting: null });
}
